// scroll-manager.js
import Lenis from 'lenis';

let lenisInstance;

// Keep track of the requestAnimationFrame ID to cancel it if needed
let rafId;
let isAnchorScrollingListenerAttached = false;

/**
 * RequestAnimationFrame callback to update Lenis.
 * @param {number} time - The current time.
 */
function raf(time) {
  if (lenisInstance) {
    lenisInstance.raf(time);
  }
  rafId = requestAnimationFrame(raf);
}

/**
 * Initializes the Lenis smooth scrolling instance.
 * @param {object} options - Lenis constructor options.
 * @returns {Lenis|null} The Lenis instance, or null if it couldn't be initialized.
 */
export function initializeScrollManager(options = {}) {
  if (lenisInstance) {
    // console.warn('Scroll manager (Lenis) already initialized.');
    return lenisInstance;
  }

  // Respect prefers-reduced-motion media query
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return null;
  }

  try {
    lenisInstance = new Lenis(options);
    // console.log('Scroll manager (Lenis) initialized.');
    
    // Start the animation frame loop
    if (rafId) cancelAnimationFrame(rafId); // Clear any existing loop
    rafId = requestAnimationFrame(raf);
  } catch (error) {
    console.error("Failed to initialize Lenis:", error);
    return null;
  }
  
  return lenisInstance;
}

export function getLenisInstance() {
  return lenisInstance;
}

export function destroyScrollManager() {
  if (lenisInstance) {
    lenisInstance.destroy(); // Lenis's internal cleanup
    lenisInstance = null;
  }
  if (rafId) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
  // console.log('Scroll manager (Lenis) destroyed.');
}

/**
 * Scroll offset that keeps a target clear of the fixed header, as it will sit
 * once the scroll ends. The header's behaviour comes from the template's CSS
 * (`$header` → `--header-position`, `--header-hide-on-scroll`): scrolling down
 * may slide it away, so the offset depends on the direction of travel.
 *
 * @param {number|null} targetY Page Y the scroll is heading for; null uses the
 *                              current direction (`data-header-direction`).
 * @returns {number} Negative offset in px (for Lenis `offset`), or 0.
 */
export function getHeaderOffset(targetY = null) {
  const header = document.getElementById('header');
  const wpAdminBar = document.getElementById('wpadminbar');
  const root = document.documentElement;
  const css = getComputedStyle(root);
  const position = css.getPropertyValue('--header-position').trim(); // '' without `$header`
  const hide = css.getPropertyValue('--header-hide-on-scroll').trim() || 'none';

  const current = window.scrollY;
  const goingDown = targetY === null
    ? root.dataset.headerDirection === 'down'
    : targetY > current;

  let offset = 0;
  if (wpAdminBar && getComputedStyle(wpAdminBar).position === 'fixed') offset += wpAdminBar.offsetHeight;

  if (header && position === 'fixed') {
    if (!goingDown || hide === 'none') offset += header.offsetHeight;
    // hide === 'all': nothing covers the target.
  } else if (header && position === '') {
    offset += header.offsetHeight; // No `$header` config: the header's height.
  }
  // position === 'absolute': the header has scrolled away with the page.
  return -offset;
}

/**
 * Page Y of an element (for `getHeaderOffset()`).
 * @param {Element} el
 * @returns {number}
 */
function pageY(el) {
  return el.getBoundingClientRect().top + window.scrollY;
}

/**
 * Calculates total header height in positive pixels.
 * @returns {number} Header height + WP admin bar height.
 */
export function getHeaderHeight() {
  return Math.abs(getHeaderOffset());
}

/**
 * Whether a URL points at the current page (same origin, path — trailing slash
 * ignored — and query string): the links this module smooth-scrolls.
 * @param {URL} url
 * @returns {boolean}
 */
export function isSamePageUrl(url) {
  const currentPath = window.location.pathname.replace(/\/$/, "") || '/';
  const linkPath = url.pathname.replace(/\/$/, "") || '/';
  return url.origin === window.location.origin && linkPath === currentPath && url.search === window.location.search;
}

/**
 * Initializes handling for same-page anchor links to scroll smoothly using Lenis.
 * @param {Lenis} lenisInstance - The initialized Lenis instance.
 */
export function initializeLenisAnchorScrolling(lenisInstance) {
  if (!lenisInstance) {
    console.error('Lenis instance not provided for anchor scrolling initialization.');
    return;
  }


  if (!isAnchorScrollingListenerAttached) {
    document.addEventListener('click', function(event) {
    // If the clicked element or an ancestor has 'data-scroll-ignore',
    // skip Lenis smooth-scroll handling for it.
    if (event.target.closest('[data-scroll-ignore]')) {
      // Only suppress default navigation for placeholder/control anchors
      // (e.g. href="#" used as a slideshow control) so they don't jump the
      // page. Genuine content links inside a scroll-ignored region (like the
      // links in slideshow copy) must still navigate normally.
      const ignoredLink = event.target.closest('a');
      if (ignoredLink) {
        const ignoredHref = ignoredLink.getAttribute('href');
        if (!ignoredHref || ignoredHref === '#') {
          event.preventDefault();
        }
      }
      // In either case, we stop the scroll manager from proceeding.
      return;
    }

    // Find the closest ancestor anchor link
    const link = event.target.closest('a');
  
    if (!link) return;

    const href = link.getAttribute('href');  
    // Must have a hash to be considered for anchor scrolling
    if (!href || href.indexOf('#') === -1) return;

    // Resolve the URL to check if it points to the current page
    const linkUrl = new URL(link.href, window.location.href);
    if (!isSamePageUrl(linkUrl)) {
      return; // Different page, let browser/Barba handle it
    }

    const hash = linkUrl.hash;
    let targetToScroll;
    const scrollOptions = {
      lock: true, // Prevent user scroll during programmatic scroll
    };

    // Handle links like href="#" or href="#top" to scroll to the top of the page
    if (!hash || hash === '#' || hash.toLowerCase() === '#top') {
      targetToScroll = 0;
    } else {
      // For other hashes, check if the target element exists on the page.
      try {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          scrollOptions.offset = getHeaderOffset(pageY(targetElement));
          targetToScroll = hash; // Use the selector string; Lenis will resolve it.
        } else {
          console.warn(`Anchor target "${hash}" not found on this page. Default link behavior will proceed.`);
          return;
        }
      } catch (e) {
        console.warn(`Invalid selector "${hash}".`);
        return;
      }
    }

    // If a valid target for Lenis is determined (0 or an existing selector)
    event.preventDefault(); // Prevent the default anchor jump
    lenisInstance.scrollTo(targetToScroll, scrollOptions);

    // Manually update URL hash if needed, as preventDefault stops it.
    if (targetToScroll !== 0 && history.pushState) {
      history.pushState(null, null, hash);
    }
    }, false); // Use event delegation on the document
    isAnchorScrollingListenerAttached = true;
  }
}

/**
 * Handles scrolling to the URL hash on page load or transition.
 * @param {Lenis} lenisInstance 
 * @param {string|null} hash - Optional hash to scroll to.
 */
export function handleHashScroll(lenisInstance, hash = null, delay = 500) {
  if (!lenisInstance) return;

  // Normalise the fragment to a bare id. Barba's url.parse() yields the fragment
  // WITHOUT a leading '#' (e.g. "purpose"), whereas window.location.hash includes
  // it ("#purpose"). Passing "purpose" straight to querySelector() would match a
  // <purpose> tag (nothing) and silently fail to scroll, so strip any leading '#'
  // and resolve by id.
  const raw = hash || window.location.hash;
  if (!raw) return;

  const targetId = raw.replace(/^#+/, '');
  if (!targetId) return;

  // `delay` lets the caller wait for the DOM to settle. On a blind transition the
  // default 500ms is a best-effort guess; callers that have already awaited media
  // loading (and lenis.resize) pass a small/zero delay for an accurate landing
  // that isn't thrown off by late-loading images reflowing content above.
  const scroll = () => {
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      lenisInstance.scrollTo(targetElement, {
        offset: getHeaderOffset(pageY(targetElement)),
        force: true,
        lock: true,
      });
    }
  };

  if (delay > 0) {
    setTimeout(scroll, delay);
  } else {
    scroll();
  }
}